import { useMultiFieldSet } from "@/components/utils/useFieldSet";
import { NonNullish } from "@/flokit/utils/comparison/non-nullish";
import {
  JobAppEducationHistoryRequest,
  JobAppEducationHistoryRequestDegree,
  JobAppSectionResponseSectionType,
} from "modules/api-docs";
import {
  EducationHistoryFields,
  educationHistoryYupSchema,
} from "modules/self-apply/validation/candidate-self-apply";
import { useMemo } from "react";
import {
  SelfApplyOnlyBaseJobAppSectionFieldSetArgs,
  SelfApplyOnlyNullishExistingJobApp,
  SelfApplyOnlyNullishJobSection,
  selfApplyOnlyBaseIsSectionRequired,
} from "./common";

export function isEducationHistoryRequired(
  jobSections: SelfApplyOnlyNullishJobSection[],
) {
  return selfApplyOnlyBaseIsSectionRequired(
    jobSections,
    JobAppSectionResponseSectionType.EDUCATION_HISTORY,
  );
}

export function extractEducationHistoryFromExistingJobApp(args: {
  existingJobApp: SelfApplyOnlyNullishExistingJobApp;
}): EducationHistoryFields[] {
  const existingAnswers: EducationHistoryFields[] = [];
  if (!args.existingJobApp?.educationHistory) {
    return existingAnswers;
  }
  const existingEducationHistory =
    args.existingJobApp.educationHistory.filter(NonNullish);

  for (const entry of existingEducationHistory) {
    const answer: EducationHistoryFields = {
      university: entry.university ?? null,
      degree: entry.degreeEnum?.toString() ?? null,
      endDate: entry.endDate ?? null,
    };

    existingAnswers.push(answer);
  }

  return existingAnswers;
}

type UseEducationHistoryFieldSetArgs =
  SelfApplyOnlyBaseJobAppSectionFieldSetArgs;
export function useEducationHistoryFieldSet({
  jobSections,
  existingJobApp,
}: UseEducationHistoryFieldSetArgs) {
  const initialValue = useMemo(() => {
    return extractEducationHistoryFromExistingJobApp({ existingJobApp });
  }, [existingJobApp]);

  return useMultiFieldSet<EducationHistoryFields>(
    initialValue,
    [initialValue],
    educationHistoryYupSchema,
    {
      minLength: isEducationHistoryRequired(jobSections) ? 1 : undefined,
    },
  );
}

function isValidDegree(
  degree: unknown,
): degree is JobAppEducationHistoryRequestDegree {
  if (!degree || typeof degree !== "string") {
    return false;
  }

  const validDegrees: string[] = Object.values(
    JobAppEducationHistoryRequestDegree,
  );
  return validDegrees.includes(degree);
}

export function educationHistoryToRequests(args: {
  fields: EducationHistoryFields[];
}): JobAppEducationHistoryRequest[] {
  return args.fields
    .map((entry) => {
      const { university, degree, endDate } = entry;

      // If degree is truth-y, it must be valid
      if (!!degree && !isValidDegree(degree)) {
        return null;
      }

      const request: JobAppEducationHistoryRequest = {
        university: university ?? undefined,
        // Needed to check again that degree is valid, since the '!!degree' check prevents TS fully inferring type of degree
        degree: isValidDegree(degree) ? degree : undefined,
        endDate: endDate ?? undefined,
        // TODO: This is not set in the self apply code; do we need it here?
        jobAppId: undefined,
      };

      return request;
    })
    .filter(NonNullish);
}
